/* eslint-disable no-nested-ternary */
/* eslint-disable react/no-array-index-key */
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import { Box, Grid, Card, Typography, useTheme } from '@mui/material';
import { sessionApi } from '../../../api/sessionApi';
import { motion } from 'framer-motion';
import CountUp from 'react-countup';
import moment from 'moment';
import LayersRounded from '@mui/icons-material/LayersRounded';
import PeopleAltRounded from '@mui/icons-material/PeopleAltRounded';
import AccountBalanceWalletRounded from '@mui/icons-material/AccountBalanceWalletRounded';
import TrendingUpRounded from '@mui/icons-material/TrendingUpRounded';
import PercentRounded from '@mui/icons-material/PercentRounded';
import SpeedRounded from '@mui/icons-material/SpeedRounded';
import ShowChartRounded from '@mui/icons-material/ShowChartRounded';
import StackedLineChartRounded from '@mui/icons-material/StackedLineChartRounded';
import WorkOutlineRounded from '@mui/icons-material/WorkOutlineRounded';
import EmojiEventsRounded from '@mui/icons-material/EmojiEventsRounded';
import PlaceRounded from '@mui/icons-material/PlaceRounded';
import DonutLargeRounded from '@mui/icons-material/DonutLargeRounded';
import FactCheckRounded from '@mui/icons-material/FactCheckRounded';
import PersonOffRounded from '@mui/icons-material/PersonOffRounded';
import EventBusyRounded from '@mui/icons-material/EventBusyRounded';
import CategoryRounded from '@mui/icons-material/CategoryRounded';
import WarningAmberRounded from '@mui/icons-material/WarningAmberRounded';
import { getSessionKanbanStatus, KANBAN_STATUSES } from './SessionFormationsKanban';
import { alpha } from '@mui/material/styles';
import { getTone, darkVeil, greyAlpha, grey, lightVeil } from '../../../theme/tones';

// Formatage FR : arrondi + séparateur de milliers ; version euros pour l'axe monétaire.
const nfFr = (n) => Math.round(n).toLocaleString('fr-FR');
const eur = (n) => `${nfFr(n)} €`;

// Mini-graphe SVG (sparkline) sans dépendance : trait fluide, épaisseur constante
// quel que soit l'étirement horizontal (vector-effect non-scaling-stroke).
const Sparkline = ({ data, color, height = 44 }) => {
  if (!data || data.length < 2) return null;

  const width = 300;
  const pad = 3;
  const max = Math.max(...data);
  const min = Math.min(...data);
  const range = max - min || 1;
  const stepX = width / (data.length - 1);
  const points = data.map((v, i) => [
    i * stepX,
    pad + (1 - (v - min) / range) * (height - 2 * pad)
  ]);

  const linePath = points
    .map(([x, y], i) => `${i === 0 ? 'M' : 'L'} ${x.toFixed(1)} ${y.toFixed(1)}`)
    .join(' ');
  const areaPath = `${linePath} L ${width} ${height} L 0 ${height} Z`;
  const gradId = `spark-${color.replace('#', '')}`;

  return (
    <Box sx={{ mt: 'auto', pt: 1.5, mx: -0.5 }}>
      <svg
        viewBox={`0 0 ${width} ${height}`}
        width="100%"
        height={height}
        preserveAspectRatio="none"
        style={{ display: 'block' }}
      >
        <defs>
          <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity="0.18" />
            <stop offset="100%" stopColor={color} stopOpacity="0" />
          </linearGradient>
        </defs>
        <path d={areaPath} fill={`url(#${gradId})`} stroke="none" />
        <path
          d={linePath}
          fill="none"
          stroke={color}
          strokeWidth={2}
          strokeLinecap="round"
          strokeLinejoin="round"
          vectorEffect="non-scaling-stroke"
        />
      </svg>
    </Box>
  );
};

Sparkline.propTypes = {
  data: PropTypes.arrayOf(PropTypes.number),
  color: PropTypes.string.isRequired,
  height: PropTypes.number
};

// Défauts de palette : série 1 = orange (jamais passée explicitement), série 2 doit
// toujours être fournie car le défaut n°2 (#0e1c2f) se lit mal en trait fin.
const CHART_COLORS = [getTone('orange').base, getTone('brand').deepest];

// Arrondit le max à un palier « lisible » : 91 → 95, 36991 → 40000.
const niceMax = (m) => {
  if (!(m > 0)) return 1;
  const p = Math.pow(10, Math.floor(Math.log10(m)));
  return (Math.ceil((m / p) * 2) / 2) * p;
};

// Graphe linéaire multi-séries — SVG inline, zéro dépendance. viewBox en unités fixes
// (0 0 800 h), le navigateur met à l'échelle. Échelle Y partagée entre toutes les séries,
// base forcée à 0 (honnêteté de l'axe ; étendue vers le négatif seulement si nécessaire).
const LineChart = ({ labels, series, height = 200, valueFormat }) => {
  const theme = useTheme();
  const [hover, setHover] = useState(null);
  const fmt = valueFormat || nfFr;

  const n = labels.length;
  if (n === 0) {
    return (
      <Box sx={{ height, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Typography sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>Aucune donnée sur la période</Typography>
      </Box>
    );
  }

  const W = 800;
  const padL = 52; // place pour « 40 000 € »
  const padR = 8;
  const padT = 12;
  const padB = 28; // labels X
  const innerW = W - padL - padR;
  const innerH = height - padT - padB;

  const allValues = series.reduce((acc, s) => acc.concat(s.data || []), []);
  const min = Math.min(0, ...allValues);
  const max = niceMax(Math.max(...allValues, 1));
  const span = max - min || 1;

  const xFor = (i) => (n === 1 ? padL + innerW / 2 : padL + (i / (n - 1)) * innerW);
  const yFor = (v) => padT + innerH - ((v - min) / span) * innerH;
  const colorFor = (s, i) => s.color || CHART_COLORS[i] || CHART_COLORS[0];
  const bandW = n > 1 ? innerW / (n - 1) : innerW;

  // Anti-chevauchement des libellés X : ~12 max affichés.
  const labelStep = Math.max(1, Math.ceil(n / 12));

  const ticks = [0, 0.25, 0.5, 0.75, 1];
  const boxW = 128;
  const boxH = 22 + series.length * 15;
  const tipX = hover !== null ? Math.min(xFor(hover) + 8, W - boxW - 2) : 0;

  return (
    <Box>
      <svg viewBox={`0 0 ${W} ${height}`} width="100%" style={{ display: 'block' }}>
        {/* Graduations + libellés Y */}
        {ticks.map((t) => {
          const v = min + t * (max - min);
          const y = yFor(v);
          return (
            <g key={t}>
              <line x1={padL} y1={y} x2={W - padR} y2={y} stroke={theme.palette.divider} strokeWidth={1} />
              <text x={padL - 6} y={y + 3} textAnchor="end" fontSize={10} fill={theme.palette.text.secondary}>{fmt(v)}</text>
            </g>
          );
        })}

        {/* Libellés X */}
        {labels.map((lab, i) => (
          (i % labelStep === 0 || i === n - 1) ? (
            <text
              key={`x-${i}`}
              x={xFor(i)}
              y={height - padB + 16}
              textAnchor={n === 1 ? 'middle' : i === 0 ? 'start' : i === n - 1 ? 'end' : 'middle'}
              fontSize={10}
              fill={theme.palette.text.secondary}
            >
              {lab}
            </text>
          ) : null
        ))}

        {/* Courbes */}
        {series.map((s, si) => {
          const d = (s.data || [])
            .map((v, i) => `${i === 0 ? 'M' : 'L'} ${xFor(i).toFixed(1)} ${yFor(v).toFixed(1)}`)
            .join(' ');
          return (
            <path key={`line-${si}`} d={d} fill="none" stroke={colorFor(s, si)} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
          );
        })}

        {/* Trait vertical du mois survolé */}
        {hover !== null && (
          <line x1={xFor(hover)} y1={padT} x2={xFor(hover)} y2={padT + innerH} stroke={theme.palette.text.disabled} strokeDasharray="3 3" strokeWidth={1} />
        )}

        {/* Points */}
        {series.map((s, si) => (s.data || []).map((v, i) => (
          <circle
            key={`pt-${si}-${i}`}
            cx={xFor(i)}
            cy={yFor(v)}
            r={hover === i ? 4 : 2.5}
            fill={colorFor(s, si)}
            stroke={theme.palette.background.paper}
            strokeWidth={1}
          />
        )))}

        {/* Zones de survol pleine hauteur, une par mois */}
        {labels.map((lab, i) => (
          <rect
            key={`hit-${i}`}
            x={xFor(i) - bandW / 2}
            y={padT}
            width={bandW}
            height={innerH}
            fill="transparent"
            onMouseEnter={() => setHover(i)}
            onMouseLeave={() => setHover(null)}
          />
        ))}

        {/* Tooltip (toutes les séries du mois) */}
        {hover !== null && (
          <g transform={`translate(${tipX}, ${padT})`} style={{ pointerEvents: 'none' }}>
            <rect width={boxW} height={boxH} rx={6} fill={theme.palette.background.paper} stroke={theme.palette.divider} strokeWidth={1} />
            <text x={8} y={15} fontSize={10} fontWeight={700} fill={theme.palette.text.primary}>{labels[hover]}</text>
            {series.map((s, si) => (
              <g key={`tip-${si}`} transform={`translate(8, ${29 + si * 15})`}>
                <circle cx={3} cy={-3} r={3} fill={colorFor(s, si)} />
                <text x={11} y={0} fontSize={10} fill={theme.palette.text.secondary}>{`${s.label} · ${fmt(s.data[hover] || 0)}`}</text>
              </g>
            ))}
          </g>
        )}
      </svg>

      {/* Légende — seulement à partir de 2 séries */}
      {series.length > 1 && (
        <Box sx={{ display: 'flex', gap: 2.5, justifyContent: 'center', mt: 1 }}>
          {series.map((s, si) => (
            <Box key={`leg-${si}`} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <Box sx={{ width: 10, height: 10, borderRadius: '3px', backgroundColor: colorFor(s, si) }} />
              <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', fontWeight: 600 }}>{s.label}</Typography>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

LineChart.propTypes = {
  labels: PropTypes.arrayOf(PropTypes.string).isRequired,
  series: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.string,
    data: PropTypes.arrayOf(PropTypes.number),
    color: PropTypes.string
  })).isRequired,
  height: PropTypes.number,
  valueFormat: PropTypes.func
};

// Tuile KPI — même gabarit visuel que src/components/dashboard/finance/DashboardKpiRow.js
// (carré d'icône coloré, libellé majuscule, valeur animée), adaptée pour afficher
// un signe explicite (+/-), un sous-titre et un sparkline optionnel.
const AnalyseCard = ({ label, value, color, icon, isCurrency, isPercent, signed, subtitle, sparkline }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const num = Number(value) || 0;

  return (
    <Card
      component={motion.div}
      whileHover={{ y: -4 }}
      sx={{
        p: 2.5,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        borderRadius: '16px',
        backgroundColor: isDark ? greyAlpha(800, 0.6) : grey(0),
        border: `1px solid ${isDark ? lightVeil(0.08) : darkVeil(0.06)}`,
        boxShadow: isDark
          ? `0 4px 20px -2px ${darkVeil(0.3)}`
          : `0 4px 20px -2px ${darkVeil(0.04)}`,
        transition: 'box-shadow 0.3s ease, border-color 0.3s ease',
        '&:hover': {
          boxShadow: isDark
            ? `0 12px 26px -6px ${darkVeil(0.5)}, 0 0 14px ${color}33`
            : `0 12px 26px -6px ${darkVeil(0.06)}, 0 0 12px ${color}26`,
          borderColor: `${color}66`
        }
      }}
    >
      {/* Icône + libellé */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 1.5 }}>
        <Box
          sx={{
            width: 38,
            height: 38,
            flexShrink: 0,
            borderRadius: '11px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: `${color}1f`,
            color
          }}
        >
          {icon}
        </Box>
        <Typography
          sx={{
            fontWeight: 700,
            letterSpacing: '0.6px',
            textTransform: 'uppercase',
            fontSize: '0.72rem',
            color: 'text.secondary'
          }}
        >
          {label}
        </Typography>
      </Box>

      {/* Valeur principale */}
      <Typography
        sx={{
          fontWeight: 800,
          color: 'text.primary',
          fontSize: '1.85rem',
          lineHeight: 1.1,
          letterSpacing: '-0.5px'
        }}
      >
        <CountUp
          end={num}
          separator=" "
          decimals={0}
          duration={1.1}
          prefix={signed && num > 0 ? '+' : ''}
          suffix={isCurrency ? ' €' : isPercent ? ' %' : ''}
        />
      </Typography>

      {/* Sous-titre */}
      {subtitle ? (
        <Typography sx={{ mt: 0.5, fontSize: '0.78rem', color: 'text.secondary', fontWeight: 500 }}>
          {subtitle}
        </Typography>
      ) : null}

      {/* Sparkline (poussé en bas de la carte) */}
      {sparkline ? <Sparkline data={sparkline} color={color} /> : null}
    </Card>
  );
};

AnalyseCard.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.number,
  color: PropTypes.string.isRequired,
  icon: PropTypes.node.isRequired,
  isCurrency: PropTypes.bool,
  isPercent: PropTypes.bool,
  signed: PropTypes.bool,
  subtitle: PropTypes.node,
  sparkline: PropTypes.arrayOf(PropTypes.number)
};

// Série chronologique agrégée : pour chaque session valide on ajoute valueFn(session)
// dans le bucket de sa date de début. Bucket par mois si le périmètre couvre au moins
// 2 mois (vue année), sinon par jour (vue mois courant par défaut). Les buckets vides
// intermédiaires sont conservés pour respecter l'échelle de temps.
const buildSeries = (rows, valueFn) => {
  const dated = rows
    .map((s) => ({ d: moment(s.start_date, 'DD/MM/YYYY', true), s }))
    .filter((x) => x.d.isValid());
  if (dated.length === 0) return [];

  const dates = dated.map((x) => x.d);
  const distinctMonths = new Set(dates.map((d) => d.format('YYYY-MM')));
  const byMonth = distinctMonths.size >= 2;
  const unit = byMonth ? 'month' : 'day';
  const fmt = byMonth ? 'YYYY-MM' : 'YYYY-MM-DD';

  const sums = new Map();
  dated.forEach(({ d, s }) => {
    const k = d.format(fmt);
    sums.set(k, (sums.get(k) || 0) + valueFn(s));
  });

  const cursor = moment.min(dates).clone().startOf(unit);
  const end = moment.max(dates).clone().startOf(unit);
  const series = [];
  let guard = 0;
  while (cursor.isSameOrBefore(end, unit) && guard < 2000) {
    series.push(sums.get(cursor.format(fmt)) || 0);
    cursor.add(1, unit);
    guard += 1;
  }
  return series;
};

const MONTHS_FR = ['janv.', 'févr.', 'mars', 'avr.', 'mai', 'juin', 'juil.', 'août', 'sept.', 'oct.', 'nov.', 'déc.'];
// "2026-01" → "janv. 26"
const fmtMois = (key) => {
  const [y, m] = key.split('-');
  return `${MONTHS_FR[parseInt(m, 10) - 1]} ${y.slice(2)}`;
};

// Évolution mensuelle : regroupe par mois de start_date (une session sans date valide est
// ignorée → la somme des courbes peut être < KPI Sessions ; une session à cheval compte
// dans son mois de début). Les mois manquants entre le premier et le dernier observés sont
// reconstruits à zéro (sinon un trou de 3 mois deviendrait un simple segment : le graphe
// mentirait). Plafonné aux 24 derniers mois. Retourne des tableaux parallèles.
const buildMonthlyEvolution = (rows) => {
  const map = new Map();
  rows.forEach((s) => {
    const d = moment(s.start_date, 'DD/MM/YYYY', true);
    if (!d.isValid()) return;
    const key = d.format('YYYY-MM');
    const acc = map.get(key) || { sessions: 0, inscrits: 0, ca: 0, benef: 0, noShows: 0 };
    acc.sessions += 1;
    acc.inscrits += parseInt(s.nombre_inscrits, 10) || 0;
    acc.ca += parseFloat(s.ca_net) || 0;
    acc.benef += parseFloat(s.resultat) || 0;
    acc.noShows += parseInt(s.no_shows, 10) || 0;
    map.set(key, acc);
  });

  const empty = { labels: [], sessions: [], inscrits: [], ca: [], benef: [] };
  if (map.size === 0) return empty;

  const keys = Array.from(map.keys()).sort();
  const cursor = moment(`${keys[0]}-01`, 'YYYY-MM-DD');
  const end = moment(`${keys[keys.length - 1]}-01`, 'YYYY-MM-DD');
  const monthKeys = [];
  let guard = 0;
  while (cursor.isSameOrBefore(end, 'month') && guard < 600) {
    monthKeys.push(cursor.format('YYYY-MM'));
    cursor.add(1, 'month');
    guard += 1;
  }

  const capped = monthKeys.slice(-24);
  const zero = { sessions: 0, inscrits: 0, ca: 0, benef: 0, noShows: 0 };
  const get = (k) => map.get(k) || zero;
  // Taux de no-show mensuel, arrondi au dixième (série unique).
  const tauxNoShowMois = (k) => {
    const m = get(k);
    return m.inscrits > 0 ? Math.round((m.noShows / m.inscrits) * 1000) / 10 : 0;
  };

  return {
    labels: capped.map(fmtMois),
    sessions: capped.map((k) => get(k).sessions),
    inscrits: capped.map((k) => get(k).inscrits),
    ca: capped.map((k) => get(k).ca),
    benef: capped.map((k) => get(k).benef),
    tauxNoShow: capped.map(tauxNoShowMois)
  };
};

// En-tête de carte graphe : petite icône + titre en majuscules (style des libellés KPI).
const ChartHeader = ({ icon, title }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
    <Box sx={{ color: getTone('orange').base, display: 'flex' }}>{icon}</Box>
    <Typography sx={{ fontWeight: 700, letterSpacing: '0.6px', textTransform: 'uppercase', fontSize: '0.72rem', color: 'text.secondary' }}>
      {title}
    </Typography>
  </Box>
);

ChartHeader.propTypes = {
  icon: PropTypes.node.isRequired,
  title: PropTypes.string.isRequired
};

// Style de carte commun aux graphes et aux panneaux.
const PANEL_CARD_SX = {
  p: 2.5,
  height: '100%',
  borderRadius: '16px',
  backgroundColor: (t) => (t.palette.mode === 'dark' ? greyAlpha(800, 0.6) : grey(0)),
  border: (t) => `1px solid ${t.palette.mode === 'dark' ? lightVeil(0.08) : darkVeil(0.06)}`,
  boxShadow: (t) => (t.palette.mode === 'dark' ? `0 4px 20px -2px ${darkVeil(0.3)}` : `0 4px 20px -2px ${darkVeil(0.04)}`)
};

// ── Agrégat partagé ── une seule forme, une seule accumulation, réutilisée par toutes les
// dimensions (profession, formation, …). Ajouter une dimension = un buildXxx de trois lignes.
// inscrits = inscritsActifs (Inscrit + InscritD, ni abandonnés ni annulés) : c'est le
// dénominateur du taux de no-show. capacite / inscritsCap ne s'incrémentent que pour les
// sessions ayant une capacité (>0) — taux de remplissage pondéré.
const emptyAgg = () => ({ sessions: 0, inscrits: 0, ca: 0, benef: 0, noShows: 0, abandons: 0, capacite: 0, inscritsCap: 0 });
const accumule = (a, s) => {
  const inscrits = parseInt(s.nombre_inscrits, 10) || 0;
  a.sessions += 1;
  a.inscrits += inscrits;
  a.ca += parseFloat(s.ca_net) || 0;
  a.benef += parseFloat(s.resultat) || 0;
  a.noShows += parseInt(s.no_shows, 10) || 0;
  a.abandons += parseInt(s.abandons, 10) || 0;
  const cap = parseInt(s.capacite_max, 10) || 0;
  if (cap > 0) {
    a.capacite += cap;
    a.inscritsCap += inscrits;
  }
};

// Deux définitions partagées par tous les panneaux d'assiduité / remplissage.
// « parmi les gens attendus, combien ne sont pas venus » : un absent reste dans inscrits
// (num. ET dénom.). Le remplissage est pondéré : inscrits cumulés / places cumulées.
const tauxNoShow = (a) => (a.inscrits > 0 ? (a.noShows / a.inscrits) * 100 : 0);
const tauxRemplissage = (a) => (a.capacite > 0 ? (a.inscritsCap / a.capacite) * 100 : 0);

// Par profession : une session est comptée INTÉGRALEMENT pour chaque profession qu'elle vise
// (s.professions = GROUP_CONCAT des formations_prices.profession). Le Set évite le double
// comptage d'une même profession listée deux fois. Totaux NON additionnables (une session
// multi-professions compte dans chaque ligne) ; une session sans professions renseignées est
// absente du panneau. Tri + longueur de barre sur les sessions.
const buildParProfession = (rows) => {
  const m = new Map();
  rows.forEach((s) => {
    if (!s.professions) return;
    const keys = new Set(s.professions.split(',').map((p) => p.trim()).filter(Boolean));
    keys.forEach((k) => {
      const a = m.get(k) || emptyAgg();
      accumule(a, s);
      m.set(k, a);
    });
  });
  return [...m.entries()]
    .map(([profession, agg]) => ({ profession, ...agg }))
    .sort((x, y) => y.sessions - x.sessions)
    .slice(0, 10);
};

// Top formations par CA : groupement sur l'id (pas le titre → deux homonymes restent distincts,
// un renommage ne scinde pas l'historique), titre capturé à la création, tri CA décroissant,
// top 10. Le bénéfice est affiché à côté pour que l'écart de marge saute aux yeux.
const buildTopFormations = (rows) => {
  const m = new Map();
  rows.forEach((s) => {
    if (s.idFormation == null) return;
    const a = m.get(s.idFormation) || { id: s.idFormation, titre: s.name, ...emptyAgg() };
    accumule(a, s);
    m.set(s.idFormation, a);
  });
  return [...m.values()].sort((x, y) => y.ca - x.ca).slice(0, 10);
};

// Bénéfice signé et coloré (+ vert / − rouge).
const signedEur = (n) => `${n > 0 ? '+' : ''}${eur(n)}`;

// Normalisation pour rapprocher un libellé de profession (formation) d'une profession
// prospect en texte libre : minuscules, sans accents (NFD + diacritiques combinants),
// parenthèses retirées (« Infirmier(ère) » → « infirmier »).
const normProfession = (s) => (s || '')
  .toString()
  .toLowerCase()
  .normalize('NFD')
  .replace(/[̀-ͯ]/g, '')
  .replace(/\(.*?\)/g, '')
  .trim();

// Somme du vivier prospect rattaché à un libellé de profession. Match : égalité normalisée
// ou préfixe bidirectionnel (garde de longueur ≥ 4) — rattrape « Médecin généraliste » →
// medecin, « Infirmier libéral » → infirmier, et fusionne les variantes sur la même clé.
// Permissif par choix (le panneau n'est de toute façon pas additionnable) ; une version plus
// robuste utiliserait un score (égalité > préfixe long > préfixe court).
const sumVivier = (profession, normalizedProspects) => {
  const target = normProfession(profession);
  if (target.length < 3) return undefined;
  let sum = 0;
  let matched = false;
  normalizedProspects.forEach(({ t, n }) => {
    if (t === target || (t.length >= 4 && target.length >= 4 && (t.startsWith(target) || target.startsWith(t)))) {
      sum += n;
      matched = true;
    }
  });
  return matched ? sum : undefined;
};

// ── Panneau gauche : Par profession × vivier ──
const ParProfessionPanel = ({ data, viverFor, totalVivier }) => {
  const maxSessions = data.reduce((mx, d) => Math.max(mx, d.sessions), 1);
  return (
    <Card sx={PANEL_CARD_SX}>
      <ChartHeader icon={<WorkOutlineRounded sx={{ fontSize: '1.15rem' }} />} title="Par profession — sessions vs vivier prospects" />
      {data.length === 0 ? (
        <Typography sx={{ fontSize: '0.8rem', color: 'text.secondary', py: 2 }}>Aucune profession ciblée sur la période.</Typography>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.75 }}>
          {data.map((d) => {
            const v = viverFor ? viverFor(d.profession) : undefined;
            return (
              <Box key={d.profession}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 1, mb: 0.6 }}>
                  <Typography noWrap sx={{ fontWeight: 700, fontSize: '0.85rem', color: 'text.primary', minWidth: 0 }}>
                    {d.profession}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
                    <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary', whiteSpace: 'nowrap' }}>
                      {`${d.sessions} sess. · ${d.inscrits} insc. · ${eur(d.ca)}`}
                    </Typography>
                    {v != null && (
                      <Box
                        sx={{
                          fontSize: '0.72rem',
                          fontWeight: 700,
                          color: 'secondary.dark',
                          backgroundColor: (theme) => alpha(theme.palette.secondary.dark, 0.10),
                          borderRadius: '8px',
                          px: 1,
                          py: 0.25,
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {`${nfFr(v)} prospects`}
                      </Box>
                    )}
                  </Box>
                </Box>
                <Box sx={{ height: 8, borderRadius: '4px', backgroundColor: (t) => (t.palette.mode === 'dark' ? lightVeil(0.06) : greyAlpha(900, 0.05)) }}>
                  <Box sx={{ height: '100%', borderRadius: '4px', width: `${(d.sessions / maxSessions) * 100}%`, background: `linear-gradient(90deg, ${getTone('amber').light} 0%, ${getTone('orange').base} 100%)` }} />
                </Box>
              </Box>
            );
          })}
        </Box>
      )}
      <Typography sx={{ mt: 2, fontSize: '0.72rem', color: 'text.secondary', lineHeight: 1.5 }}>
        {`Vivier = prospects du groupe${totalVivier != null ? ` (${nfFr(totalVivier)} au total)` : ''}. Un gros vivier avec peu de sessions = opportunité de programmation.`}
      </Typography>
    </Card>
  );
};

ParProfessionPanel.propTypes = {
  data: PropTypes.array.isRequired,
  viverFor: PropTypes.func,
  totalVivier: PropTypes.number
};

// ── Panneau droit : Top formations par CA ──
const numCellSx = { textAlign: 'right', py: 1, fontSize: '0.8rem', borderBottom: '1px solid', borderColor: 'divider', whiteSpace: 'nowrap' };
const TopFormationsPanel = ({ data }) => (
  <Card sx={PANEL_CARD_SX}>
    <ChartHeader icon={<EmojiEventsRounded sx={{ fontSize: '1.15rem' }} />} title="Top formations (par CA)" />
    {data.length === 0 ? (
      <Typography sx={{ fontSize: '0.8rem', color: 'text.secondary', py: 2 }}>Aucune formation sur la période.</Typography>
    ) : (
      <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
        <Box component="thead">
          <Box
            component="tr"
            sx={{ '& th': { fontSize: '0.66rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.4px', color: 'text.secondary', pb: 1, borderBottom: '1px solid', borderColor: 'divider' } }}
          >
            <Box component="th" sx={{ textAlign: 'left' }}>Formation</Box>
            <Box component="th" sx={{ textAlign: 'right', width: 54 }}>Sess.</Box>
            <Box component="th" sx={{ textAlign: 'right', width: 54 }}>Insc.</Box>
            <Box component="th" sx={{ textAlign: 'right', width: 84 }}>CA</Box>
            <Box component="th" sx={{ textAlign: 'right', width: 92 }}>Bénéfice</Box>
          </Box>
        </Box>
        <Box component="tbody">
          {data.map((f) => (
            <Box component="tr" key={f.id}>
              <Box
                component="td"
                title={f.titre}
                sx={{ maxWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', pr: 1.5, py: 1, fontSize: '0.8rem', fontWeight: 600, color: 'text.primary', borderBottom: '1px solid', borderColor: 'divider' }}
              >
                {f.titre}
              </Box>
              <Box component="td" sx={{ ...numCellSx, color: 'text.secondary' }}>{f.sessions}</Box>
              <Box component="td" sx={{ ...numCellSx, color: 'text.secondary' }}>{f.inscrits}</Box>
              <Box component="td" sx={{ ...numCellSx, fontWeight: 600, color: 'text.primary' }}>{eur(f.ca)}</Box>
              <Box component="td" sx={{ ...numCellSx, fontWeight: 700, color: f.benef < 0 ? getTone('rose').base : getTone('emerald').base }}>{signedEur(f.benef)}</Box>
            </Box>
          ))}
        </Box>
      </Box>
    )}
  </Card>
);

TopFormationsPanel.propTypes = {
  data: PropTypes.array.isRequired
};

// ── Département déduit d'un code postal ── DOM-TOM (97x/98x) sur 3 chiffres, Corse scindée
// à 20200 en 2A/2B, sinon 2 chiffres. Le CP est cherché dans du texte libre (\b\d{5}\b).
const deptFromCP = (value) => {
  if (!value) return null;
  const m = value.toString().match(/\b(\d{5})\b/);
  if (!m) return null;
  const code = m[1];
  if (code.startsWith('97') || code.startsWith('98')) return code.slice(0, 3);
  if (code.startsWith('20')) return Number(code) < 20200 ? '2A' : '2B';
  return code.slice(0, 2);
};
// Cascade de repli lieu → ville → nom de lieu (le premier CP exploitable gagne). Sans CP
// exploitable, la session renvoie null et disparaît du panneau.
const deptFromSession = (s) =>
  deptFromCP(s.meeting_place) || deptFromCP(s.meeting_town) || deptFromCP(s.meeting_place_name);

// Top départements : agrégat sur le département, tri par sessions, top 12. Construit en
// itérant sur les sessions → un département sans session n'apparaît pas (limite assumée du
// panneau, cf. commentaire de la référence). Le ★ (gros vivier, peu de sessions) ne peut
// donc apparaître que dans la queue du tableau.
const buildParDept = (rows) => {
  const m = new Map();
  rows.forEach((s) => {
    const dept = deptFromSession(s);
    if (!dept) return;
    const a = m.get(dept) || emptyAgg();
    accumule(a, s);
    m.set(dept, a);
  });
  return [...m.entries()]
    .map(([dept, agg]) => ({ dept, ...agg }))
    .sort((x, y) => y.sessions - x.sessions)
    .slice(0, 12);
};

// Formats : agrégat sur la modalité (formations.format), sessions sans modalité exclues.
const buildParFormat = (rows) => {
  const m = new Map();
  rows.forEach((s) => {
    const fmt = (s.format || '').trim();
    if (!fmt) return;
    const a = m.get(fmt) || emptyAgg();
    accumule(a, s);
    m.set(fmt, a);
  });
  return [...m.entries()]
    .map(([format, agg]) => ({ format, ...agg }))
    .sort((x, y) => y.sessions - x.sessions);
};

// Statuts : classification du cycle de vie via getSessionKanbanStatus (la même que le Kanban).
// L'ordre d'affichage vient du référentiel KANBAN_STATUSES (cycle de vie), pas des effectifs ;
// les statuts absents sont masqués.
const buildStatuts = (rows) => {
  const m = new Map();
  rows.forEach((s) => {
    const st = getSessionKanbanStatus(s);
    m.set(st, (m.get(st) || 0) + 1);
  });
  return KANBAN_STATUSES.filter((k) => m.has(k.id)).map((k) => ({ id: k.id, label: k.label, color: k.color, n: m.get(k.id) }));
};

// Remplissage : quatre paliers en cascade (Complet ≥ 1, 80–99 %, 50–79 %, < 50 %). Les
// sessions sans capacite_max sont ignorées → tant que la colonne n'existe pas, tout reste à 0
// (le panneau affiche alors un message plutôt que quatre zéros trompeurs).
const REMPL_BUCKETS = ['< 50 %', '50–79 %', '80–99 %', 'Complet'];
const buildRemplissage = (rows) => {
  const buckets = [0, 0, 0, 0];
  rows.forEach((s) => {
    const cap = parseInt(s.capacite_max, 10) || 0;
    if (cap <= 0) return;
    const t = (parseInt(s.nombre_inscrits, 10) || 0) / cap;
    if (t >= 1) buckets[3] += 1;
    else if (t >= 0.8) buckets[2] += 1;
    else if (t >= 0.5) buckets[1] += 1;
    else buckets[0] += 1;
  });
  return REMPL_BUCKETS.map((label, i) => ({ label, value: buckets[i] }));
};

// ── Donut SVG sans dépendance ── un seul <circle> par segment (strokeDasharray = la part,
// strokeDashoffset cumulé = somme des parts précédentes), rotate(-90) pour démarrer à midi,
// cercle gris de fond sous les arcs. Se redimensionne sans recalcul.
const DONUT_COLORS = [getTone('orange').base, getTone('brand').deepest, getTone('emerald').base, getTone('blue').base, getTone('violet').base, getTone('rose').base];
const DonutChart = ({ segments, centerValue, centerLabel }) => {
  const theme = useTheme();
  const total = segments.reduce((s, x) => s + x.value, 0);
  const size = 150;
  const stroke = 18;
  const r = (size - stroke) / 2;
  const c = size / 2;
  const circumf = 2 * Math.PI * r;
  let cumul = 0;
  return (
    <svg viewBox={`0 0 ${size} ${size}`} width={size} height={size} style={{ flexShrink: 0 }}>
      <g transform={`rotate(-90 ${c} ${c})`}>
        <circle cx={c} cy={c} r={r} fill="none" stroke={theme.palette.divider} strokeWidth={stroke} />
        {total > 0 && segments.map((seg, i) => {
          const portion = seg.value / total;
          const dasharray = `${portion * circumf} ${circumf}`;
          const offset = -cumul * circumf;
          cumul += portion;
          return (
            <circle
              key={i}
              cx={c}
              cy={c}
              r={r}
              fill="none"
              stroke={seg.color || DONUT_COLORS[i % DONUT_COLORS.length]}
              strokeWidth={stroke}
              strokeDasharray={dasharray}
              strokeDashoffset={offset}
            />
          );
        })}
      </g>
      <text x={c} y={c - 1} textAnchor="middle" fontSize={24} fontWeight={800} fill={theme.palette.text.primary}>{centerValue}</text>
      <text x={c} y={c + 16} textAnchor="middle" fontSize={10} fill={theme.palette.text.secondary}>{centerLabel}</text>
    </svg>
  );
};

DonutChart.propTypes = {
  segments: PropTypes.array.isRequired,
  centerValue: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  centerLabel: PropTypes.string
};

// ── Panneau : Top départements × vivier ──
const TopDepartementsPanel = ({ data, viverFor }) => (
  <Card sx={PANEL_CARD_SX}>
    <ChartHeader icon={<PlaceRounded sx={{ fontSize: '1.15rem' }} />} title="Top départements — sessions vs vivier" />
    {data.length === 0 ? (
      <Typography sx={{ fontSize: '0.8rem', color: 'text.secondary', py: 2 }}>Aucune session géolocalisée sur la période.</Typography>
    ) : (
      <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
        <Box component="thead">
          <Box
            component="tr"
            sx={{ '& th': { fontSize: '0.66rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.4px', color: 'text.secondary', pb: 1, borderBottom: '1px solid', borderColor: 'divider' } }}
          >
            <Box component="th" sx={{ textAlign: 'left' }}>Dépt</Box>
            <Box component="th" sx={{ textAlign: 'right', width: 52 }}>Sess.</Box>
            <Box component="th" sx={{ textAlign: 'right' }}>CA</Box>
            <Box component="th" sx={{ textAlign: 'right' }}>Prospects</Box>
          </Box>
        </Box>
        <Box component="tbody">
          {data.map((d) => {
            const v = viverFor ? viverFor(d.dept) : undefined;
            const star = v != null && v > 200 && d.sessions <= 2;
            return (
              <Box component="tr" key={d.dept}>
                <Box component="td" sx={{ ...numCellSx, textAlign: 'left', fontWeight: 700, color: 'text.primary' }}>{d.dept}</Box>
                <Box component="td" sx={{ ...numCellSx, color: 'secondary.dark', fontWeight: 600 }}>{d.sessions}</Box>
                <Box component="td" sx={{ ...numCellSx, color: 'text.primary' }}>{eur(d.ca)}</Box>
                <Box component="td" sx={{ ...numCellSx, fontWeight: star ? 700 : 500, color: star ? getTone('blue').color : 'text.secondary' }}>
                  {v != null ? `${nfFr(v)}${star ? ' ★' : ''}` : '—'}
                </Box>
              </Box>
            );
          })}
        </Box>
      </Box>
    )}
    <Typography sx={{ mt: 2, fontSize: '0.72rem', color: 'text.secondary' }}>★ = gros vivier de prospects, peu de sessions.</Typography>
  </Card>
);

TopDepartementsPanel.propTypes = {
  data: PropTypes.array.isRequired,
  viverFor: PropTypes.func
};

// ── Panneau : Formats (donut) ──
const FormatsPanel = ({ data }) => {
  const segments = data.map((f, i) => ({ label: f.format, value: f.sessions, color: DONUT_COLORS[i % DONUT_COLORS.length] }));
  const total = segments.reduce((s, x) => s + x.value, 0);
  return (
    <Card sx={PANEL_CARD_SX}>
      <ChartHeader icon={<DonutLargeRounded sx={{ fontSize: '1.15rem' }} />} title="Formats" />
      {segments.length === 0 ? (
        <Typography sx={{ fontSize: '0.8rem', color: 'text.secondary', py: 2 }}>Aucune modalité renseignée sur la période.</Typography>
      ) : (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5, flexWrap: 'wrap' }}>
          <DonutChart segments={segments} centerValue={total} centerLabel="sessions" />
          <Box sx={{ flex: 1, minWidth: 150 }}>
            {segments.map((seg) => (
              <Box key={seg.label} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, py: 0.6 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                  <Box sx={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: seg.color, flexShrink: 0 }} />
                  <Typography noWrap sx={{ fontSize: '0.82rem', fontWeight: 600, color: 'text.primary' }}>{seg.label}</Typography>
                </Box>
                <Typography sx={{ fontSize: '0.8rem', color: 'text.secondary', whiteSpace: 'nowrap' }}>
                  {`${seg.value} (${total > 0 ? Math.round((seg.value / total) * 100) : 0} %)`}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      )}
    </Card>
  );
};

FormatsPanel.propTypes = {
  data: PropTypes.array.isRequired
};

// ── Panneau : Statuts & remplissage ──
const StatutsRemplissagePanel = ({ statuts, remplissage }) => {
  const maxStatut = statuts.reduce((mx, s) => Math.max(mx, s.n), 1);
  const totalMesure = remplissage.reduce((s, b) => s + b.value, 0);
  const maxRempl = remplissage.reduce((mx, b) => Math.max(mx, b.value), 1);
  const trackSx = { height: 8, borderRadius: '4px', backgroundColor: (t) => (t.palette.mode === 'dark' ? lightVeil(0.06) : greyAlpha(900, 0.05)) };
  return (
    <Card sx={PANEL_CARD_SX}>
      <ChartHeader icon={<SpeedRounded sx={{ fontSize: '1.15rem' }} />} title="Statuts & remplissage" />
      {statuts.length === 0 ? (
        <Typography sx={{ fontSize: '0.8rem', color: 'text.secondary', py: 1 }}>Aucune session sur la période.</Typography>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {statuts.map((s) => (
            <Box key={s.id}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.4 }}>
                <Typography sx={{ fontSize: '0.82rem', fontWeight: 600, color: 'text.primary' }}>{s.label}</Typography>
                <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: 'text.primary' }}>{s.n}</Typography>
              </Box>
              <Box sx={trackSx}>
                <Box sx={{ height: '100%', borderRadius: '4px', width: `${(s.n / maxStatut) * 100}%`, backgroundColor: s.color }} />
              </Box>
            </Box>
          ))}
        </Box>
      )}

      <Typography sx={{ mt: 3, mb: 1.25, fontSize: '0.66rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.4px', color: 'text.secondary' }}>
        Remplissage
      </Typography>
      {totalMesure === 0 ? (
        <Typography sx={{ fontSize: '0.78rem', color: 'text.secondary' }}>Capacité non renseignée sur les sessions.</Typography>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {remplissage.map((b) => (
            <Box key={b.label}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.4 }}>
                <Typography sx={{ fontSize: '0.82rem', fontWeight: 600, color: 'text.primary' }}>{b.label}</Typography>
                <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: 'text.primary' }}>{b.value}</Typography>
              </Box>
              <Box sx={trackSx}>
                <Box sx={{ height: '100%', borderRadius: '4px', width: `${(b.value / maxRempl) * 100}%`, backgroundColor: 'success.main' }} />
              </Box>
            </Box>
          ))}
        </Box>
      )}
    </Card>
  );
};

StatutsRemplissagePanel.propTypes = {
  statuts: PropTypes.array.isRequired,
  remplissage: PropTypes.array.isRequired
};

// ── Assiduité / no-show ──
const SUBLABEL_SX = { fontSize: '0.66rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.4px', color: 'text.secondary' };
const BAR_TRACK_SX = { height: 6, borderRadius: '3px', backgroundColor: (t) => (t.palette.mode === 'dark' ? lightVeil(0.06) : greyAlpha(900, 0.05)) };
const noShowColor = (t) => (t > 10 ? getTone('rose').base : t > 5 ? getTone('amber').base : getTone('emerald').base);
const remplissageColor = (t) => (t >= 80 ? getTone('emerald').base : t >= 50 ? getTone('amber').base : getTone('rose').base);

// Ventilation par jour de semaine : la date est reconstruite en Date locale (moment évite le
// décalage UTC de new Date("YYYY-MM-DD")). Lundi en tête, dimanche en queue, jours sans inscrit
// retirés. Les inscrits sont attribués au jour de DÉBUT de la session.
const JOURS_FR = ['Dim.', 'Lun.', 'Mar.', 'Mer.', 'Jeu.', 'Ven.', 'Sam.'];
const buildParJour = (rows) => {
  const jours = Array.from({ length: 7 }, () => ({ inscrits: 0, noShows: 0 }));
  rows.forEach((s) => {
    const d = moment(s.start_date, 'DD/MM/YYYY', true);
    if (!d.isValid()) return;
    const j = d.day(); // 0 = dimanche
    jours[j].inscrits += parseInt(s.nombre_inscrits, 10) || 0;
    jours[j].noShows += parseInt(s.no_shows, 10) || 0;
  });
  return [1, 2, 3, 4, 5, 6, 0]
    .map((j) => ({ label: JOURS_FR[j], inscrits: jours[j].inscrits, noShows: jours[j].noShows }))
    .filter((x) => x.inscrits > 0)
    .map((x) => ({ ...x, taux: x.inscrits > 0 ? (x.noShows / x.inscrits) * 100 : 0 }));
};

// Chaîne no-show : filtre ≥ 5 inscrits (un seul absent ne fait pas un taux spectaculaire),
// tri par taux décroissant (le pire en tête), slice optionnel. Tri stable → à taux égaux,
// l'ordre d'entrée (par sessions) est conservé.
const toNoShowItems = (list, labelFn, limit) => {
  const items = list
    .filter((a) => a.inscrits >= 5)
    .sort((x, y) => tauxNoShow(y) - tauxNoShow(x))
    .map((a) => ({ label: labelFn(a), taux: tauxNoShow(a), noShows: a.noShows, inscrits: a.inscrits }));
  return limit ? items.slice(0, limit) : items;
};

// Barre de taux (no-show) : taux coloré par seuil (rouge > 10 %, ambre > 5 %, vert sinon),
// détail (absents/inscrits), jauge rouge amplifiée ×2,5 (sature à 40 %) pour étaler la plage
// utile 0–20 % sur la moitié de la largeur.
const TauxBar = ({ label, taux, noShows, inscrits }) => (
  <Box sx={{ py: 0.55 }}>
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 1, mb: 0.4 }}>
      <Typography noWrap sx={{ fontSize: '0.82rem', fontWeight: 600, color: 'text.primary', minWidth: 0 }}>{label}</Typography>
      <Typography sx={{ fontSize: '0.8rem', whiteSpace: 'nowrap', flexShrink: 0 }}>
        <Box component="span" sx={{ fontWeight: 700, color: noShowColor(taux) }}>{`${Math.round(taux)} %`}</Box>
        <Box component="span" sx={{ color: 'text.secondary', ml: 0.5 }}>{`(${noShows}/${inscrits})`}</Box>
      </Typography>
    </Box>
    <Box sx={BAR_TRACK_SX}>
      <Box sx={{ height: '100%', borderRadius: '3px', width: `${Math.min(100, taux * 2.5)}%`, backgroundColor: 'error.main' }} />
    </Box>
  </Box>
);

TauxBar.propTypes = {
  label: PropTypes.string.isRequired,
  taux: PropTypes.number.isRequired,
  noShows: PropTypes.number.isRequired,
  inscrits: PropTypes.number.isRequired
};

// Barre de remplissage : symétrique de TauxBar, couleurs inversées (vert ≥ 80 %, ambre ≥ 50 %,
// rouge sinon — un taux élevé est une bonne nouvelle), pas d'amplification (0–100 utile).
const RemplissageBar = ({ label, taux, inscritsCap, capacite }) => (
  <Box sx={{ py: 0.55 }}>
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 1, mb: 0.4 }}>
      <Typography noWrap sx={{ fontSize: '0.82rem', fontWeight: 600, color: 'text.primary', minWidth: 0 }}>{label}</Typography>
      <Typography sx={{ fontSize: '0.8rem', whiteSpace: 'nowrap', flexShrink: 0 }}>
        <Box component="span" sx={{ fontWeight: 700, color: remplissageColor(taux) }}>{`${Math.round(taux)} %`}</Box>
        <Box component="span" sx={{ color: 'text.secondary', ml: 0.5 }}>{`(${inscritsCap}/${capacite})`}</Box>
      </Typography>
    </Box>
    <Box sx={BAR_TRACK_SX}>
      <Box sx={{ height: '100%', borderRadius: '3px', width: `${Math.min(100, taux)}%`, backgroundColor: remplissageColor(taux) }} />
    </Box>
  </Box>
);

RemplissageBar.propTypes = {
  label: PropTypes.string.isRequired,
  taux: PropTypes.number.isRequired,
  inscritsCap: PropTypes.number.isRequired,
  capacite: PropTypes.number.isRequired
};

// Petite tuile de stat (assiduité globale) : teinte de fond + couleur selon le tone.
const TONE_COLORS = { positive: getTone('emerald').base, warning: getTone('amber').base, negative: getTone('rose').base, neutral: getTone('neutral').base };
const MiniStat = ({ label, value, tone }) => {
  const color = TONE_COLORS[tone] || TONE_COLORS.neutral;
  return (
    <Box sx={{ p: 1.5, borderRadius: '12px', backgroundColor: `${color}14`, border: `1px solid ${color}2e` }}>
      <Typography sx={{ ...SUBLABEL_SX, fontSize: '0.62rem' }}>{label}</Typography>
      <Typography sx={{ fontSize: '1.35rem', fontWeight: 800, color, lineHeight: 1.2, mt: 0.3 }}>{value}</Typography>
    </Box>
  );
};

MiniStat.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  tone: PropTypes.string
};

// ── Panneau : Assiduité globale (4 MiniStat + courbe no-show mensuel) ──
const AssiduiteGlobalePanel = ({ totals, taux, tauxAbandon, evolution }) => (
  <Card sx={PANEL_CARD_SX}>
    <ChartHeader icon={<FactCheckRounded sx={{ fontSize: '1.15rem' }} />} title="Assiduité globale" />
    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.25 }}>
      <MiniStat label="No-shows" value={totals.noShows} tone={totals.noShows > 0 ? 'negative' : 'positive'} />
      <MiniStat label="Taux no-show" value={`${Math.round(taux)} %`} tone={taux > 10 ? 'negative' : taux > 5 ? 'warning' : 'positive'} />
      <MiniStat label="Abandons" value={totals.abandons} tone="warning" />
      <MiniStat label="Taux d'abandon" value={`${Math.round(tauxAbandon)} %`} tone="warning" />
    </Box>
    <Typography sx={{ ...SUBLABEL_SX, mt: 2.5, mb: 1 }}>Taux de no-show mensuel</Typography>
    <LineChart
      labels={evolution.labels}
      height={150}
      valueFormat={(n) => `${n} %`}
      series={[{ label: 'Taux no-show', data: evolution.tauxNoShow, color: getTone('rose').base }]}
    />
  </Card>
);

AssiduiteGlobalePanel.propTypes = {
  totals: PropTypes.object.isRequired,
  taux: PropTypes.number.isRequired,
  tauxAbandon: PropTypes.number.isRequired,
  evolution: PropTypes.object.isRequired
};

// ── Panneau générique : liste de taux no-show (profession / département) ──
const NoShowListPanel = ({ icon, title, items, footer }) => (
  <Card sx={PANEL_CARD_SX}>
    <ChartHeader icon={icon} title={title} />
    {items.length === 0 ? (
      <Typography sx={{ fontSize: '0.8rem', color: 'text.secondary', py: 1 }}>Aucun segment (≥ 5 inscrits) sur la période.</Typography>
    ) : (
      items.map((it) => <TauxBar key={it.label} {...it} />)
    )}
    {footer ? <Typography sx={{ mt: 1.5, fontSize: '0.72rem', color: 'text.secondary' }}>{footer}</Typography> : null}
  </Card>
);

NoShowListPanel.propTypes = {
  icon: PropTypes.node.isRequired,
  title: PropTypes.string.isRequired,
  items: PropTypes.array.isRequired,
  footer: PropTypes.node
};

// ── Panneau : No-show par format & jour ──
const NoShowFormatJourPanel = ({ formatItems, jourItems }) => (
  <Card sx={PANEL_CARD_SX}>
    <ChartHeader icon={<EventBusyRounded sx={{ fontSize: '1.15rem' }} />} title="No-show par format & jour" />
    {formatItems.length === 0 ? (
      <Typography sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>Aucun format (≥ 5 inscrits).</Typography>
    ) : (
      formatItems.map((it) => <TauxBar key={it.label} {...it} />)
    )}
    <Typography sx={{ ...SUBLABEL_SX, mt: 2, mb: 1 }}>Par jour de session</Typography>
    {jourItems.length === 0 ? (
      <Typography sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>Aucun inscrit daté sur la période.</Typography>
    ) : (
      jourItems.map((it) => <TauxBar key={it.label} {...it} />)
    )}
  </Card>
);

NoShowFormatJourPanel.propTypes = {
  formatItems: PropTypes.array.isRequired,
  jourItems: PropTypes.array.isRequired
};

// ── Panneau générique : remplissage par dimension (croissant, les premiers à travailler) ──
const RemplissagePanel = ({ icon, title, data, fallback, footer }) => {
  const items = data
    .filter((a) => a.capacite >= 10)
    .sort((x, y) => tauxRemplissage(x) - tauxRemplissage(y));
  return (
    <Card sx={PANEL_CARD_SX}>
      <ChartHeader icon={icon} title={title} />
      {items.length === 0 ? (
        <Typography sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>{fallback}</Typography>
      ) : (
        items.map((a) => (
          <RemplissageBar
            key={a.dept || a.profession || a.format}
            label={a.dept ? `Dépt ${a.dept}` : (a.profession || a.format)}
            taux={tauxRemplissage(a)}
            inscritsCap={a.inscritsCap}
            capacite={a.capacite}
          />
        ))
      )}
      <Typography sx={{ mt: 1.5, fontSize: '0.72rem', color: 'text.secondary' }}>{footer}</Typography>
    </Card>
  );
};

RemplissagePanel.propTypes = {
  icon: PropTypes.node.isRequired,
  title: PropTypes.string.isRequired,
  data: PropTypes.array.isRequired,
  fallback: PropTypes.string.isRequired,
  footer: PropTypes.string
};

// ── Rentabilité (3 paliers) ──
// Le palier est un seuil relatif avec plancher absolu : 5 % du CA, jamais moins de 100 €.
// La bande [-seuil, +seuil] est « à l'équilibre » — assez faible pour que la classer rentable
// serait trompeur. C'est ce qui isole la masse intermédiaire d'un simple positif/négatif.
const TIER_LABELS = { profit: 'Rentable', breakeven: "À l'équilibre", loss: 'En perte' };
const sessionTier = (s) => {
  const revenus = parseFloat(s.ca_net) || 0;
  const benefice = parseFloat(s.resultat) || 0;
  const seuil = Math.max(100, revenus * 0.05);
  if (benefice > seuil) return 'profit';
  if (benefice < -seuil) return 'loss';
  return 'breakeven';
};

// Un seul mémo pour les 3 panneaux : donut (ordre écrit en dur profit/breakeven/loss, position
// fixe quelle que soit la taille), top 5 et flop 5. Le flop est POSITIONNEL (5 dernières lignes
// du classement quel que soit leur signe), pas conditionnel : d'où d'éventuels bénéfices ≥ 0 en
// bas de liste si le périmètre compte moins de 5 sessions en perte.
const buildRentabilite = (rows) => {
  const withTier = rows.map((s) => ({
    id: s.id,
    titre: s.name,
    benefice: parseFloat(s.resultat) || 0,
    tier: sessionTier(s)
  }));
  const order = ['profit', 'breakeven', 'loss'];
  const donut = order.map((t) => ({ tier: t, label: TIER_LABELS[t], value: withTier.filter((r) => r.tier === t).length }));
  const tries = [...withTier].sort((a, b) => b.benefice - a.benefice);
  return { donut, top: tries.slice(0, 5), flop: tries.slice(-5).reverse() };
};

// ── Panneau : Rentabilité (donut) ── centre = marge moyenne GLOBALE (bénéfice/CA en euros),
// pas un total de sessions : les arcs comptent des sessions, le centre pèse des euros.
const RentabilitePanel = ({ donut, marge }) => {
  const segments = donut.map((d, i) => ({ label: d.label, value: d.value, color: DONUT_COLORS[i % DONUT_COLORS.length] }));
  const total = segments.reduce((s, x) => s + x.value, 0);
  return (
    <Card sx={PANEL_CARD_SX}>
      <ChartHeader icon={<DonutLargeRounded sx={{ fontSize: '1.15rem' }} />} title="Rentabilité" />
      {total === 0 ? (
        <Typography sx={{ fontSize: '0.8rem', color: 'text.secondary', py: 2 }}>Aucune session sur la période.</Typography>
      ) : (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5, flexWrap: 'wrap' }}>
          <DonutChart segments={segments} centerValue={`${Math.round(marge)} %`} centerLabel="marge moy." />
          <Box sx={{ flex: 1, minWidth: 150 }}>
            {segments.map((seg) => (
              <Box key={seg.label} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, py: 0.6 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                  <Box sx={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: seg.color, flexShrink: 0 }} />
                  <Typography noWrap sx={{ fontSize: '0.82rem', fontWeight: 600, color: 'text.primary' }}>{seg.label}</Typography>
                </Box>
                <Typography sx={{ fontSize: '0.8rem', color: 'text.secondary', whiteSpace: 'nowrap' }}>
                  {`${seg.value} (${total > 0 ? Math.round((seg.value / total) * 100) : 0} %)`}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      )}
    </Card>
  );
};

RentabilitePanel.propTypes = {
  donut: PropTypes.array.isRequired,
  marge: PropTypes.number.isRequired
};

// ── Panneau : mini-liste de sessions (Top 5 / à surveiller) ── lignes cliquables → détail
// session. Ce sont des SESSIONS (pas des formations) : un même titre peut apparaître deux fois.
const SessionListPanel = ({ icon, title, items, onOpen }) => (
  <Card sx={PANEL_CARD_SX}>
    <ChartHeader icon={icon} title={title} />
    {items.length === 0 ? (
      <Typography sx={{ fontSize: '0.9rem', color: 'text.secondary' }}>—</Typography>
    ) : (
      items.map((it) => {
        const positif = it.benefice >= 0;
        return (
          <Box
            component="button"
            type="button"
            key={it.id}
            onClick={() => onOpen(it.id)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1.5,
              width: '100%',
              border: 'none',
              background: 'transparent',
              cursor: 'pointer',
              textAlign: 'left',
              px: 0,
              py: 0.9,
              borderBottom: '1px solid',
              borderColor: 'divider',
              '&:hover': { '& .stitre': { color: 'secondary.main' } }
            }}
          >
            <Typography className="stitre" noWrap sx={{ fontSize: '0.82rem', fontWeight: 500, color: 'text.primary', minWidth: 0, transition: 'color 0.15s' }} title={it.titre}>
              {it.titre}
            </Typography>
            <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, whiteSpace: 'nowrap', flexShrink: 0, color: positif ? getTone('emerald').base : getTone('rose').base }}>
              {`${positif ? '+' : ''}${eur(it.benefice)}`}
            </Typography>
          </Box>
        );
      })
    )}
  </Card>
);

SessionListPanel.propTypes = {
  icon: PropTypes.node.isRequired,
  title: PropTypes.string.isRequired,
  items: PropTypes.array.isRequired,
  onOpen: PropTypes.func.isRequired
};

const SessionFormationsAnalyse = ({ sessions }) => {
  // Vivier prospects par profession — un seul appel au montage, jamais refait (indépendant
  // des filtres : c'est l'offre totale du groupe). Garde-fou anti-setState après démontage.
  const [vivier, setVivier] = useState({ byProfession: [], total: null });
  const [vivierDept, setVivierDept] = useState({ byDepartment: [], total: null });
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const token = localStorage.getItem('accessToken');
        const [prof, dept] = await Promise.all([
          sessionApi.getProspectsByProfession(token),
          sessionApi.getProspectsByDepartment(token)
        ]);
        if (!alive) return;
        if (prof) {
          setVivier({ byProfession: prof.byProfession || [], total: typeof prof.total === 'number' ? prof.total : null });
        }
        if (dept) {
          setVivierDept({ byDepartment: dept.byDepartment || [], total: typeof dept.total === 'number' ? dept.total : null });
        }
      } catch (err) {
        console.error(err);
      }
    })();
    return () => { alive = false; };
  }, []);

  const navigate = useNavigate();
  const rows = (sessions || []).filter((s) => s.name !== null);

  // SESSIONS — nombre de sessions chargées (hors lignes sans nom).
  const sessionsCount = rows.length;

  // Sessions annulées (colonne « Annulée » du Kanban : status === 'Annulée').
  const cancelledCount = rows.filter((s) => s.status === 'Annulée').length;
  const cancelledLabel = `${cancelledCount} annulée${cancelledCount > 1 ? 's' : ''}`;

  const sessionsSeries = buildSeries(rows, () => 1);

  // INSCRITS — somme des inscrits comptés (Inscrit + InscritD), pré-calculée par
  // session dans nombre_inscrits. La moyenne « / session » divise par TOUTES les
  // sessions (annulées comprises) : c'est le taux de remplissage réel du portefeuille.
  const inscritsTotal = rows.reduce((total, s) => total + (parseInt(s.nombre_inscrits, 10) || 0), 0);
  const avgPerSession = sessionsCount > 0 ? inscritsTotal / sessionsCount : 0;
  const inscritsSeries = buildSeries(rows, (s) => parseInt(s.nombre_inscrits, 10) || 0);

  // CA PRÉVISIONNEL — somme des revenus « inscrits » (statuts Inscrit + InscritD),
  // le prix négocié de l'inscription primant sur le prix de place ; le backend le
  // pré-calcule par session dans ca_net (cf. SessionModel::fetchSessionFormations).
  const caPrevisionnel = rows.reduce((total, s) => total + (parseFloat(s.ca_net) || 0), 0);
  const caSeries = buildSeries(rows, (s) => parseFloat(s.ca_net) || 0);

  // RÉSULTAT CUMULÉ — somme des bénéfices (ca_net − charge), déjà exposé en resultat.
  const resultatCumule = rows.reduce((total, s) => total + (parseFloat(s.resultat) || 0), 0);
  const resultatSeries = buildSeries(rows, (s) => parseFloat(s.resultat) || 0);

  const resultColor = resultatCumule < 0 ? getTone('rose').base : getTone('emerald').base;

  // MARGE MOYENNE — ratio GLOBAL bénéfice / CA (et non une moyenne des marges par
  // session : une moyenne de % donnerait le même poids à une session à 800 € qu'à
  // une à 40 000 €). Seuils : vert ≥ 20 %, ambre ≥ 0, rouge en dessous.
  const marge = caPrevisionnel > 0 ? (resultatCumule / caPrevisionnel) * 100 : 0;
  const margeColor = marge >= 20 ? getTone('emerald').base : marge >= 0 ? getTone('amber').base : getTone('rose').base;

  // REMPLISSAGE MOYEN — inscrits / capacité, pondéré (somme des inscrits ÷ somme des
  // capacités), uniquement sur les sessions ayant une capacité renseignée. La table
  // sessions n'a pas (encore) de colonne capacite_max et la requête de liste ne la
  // renvoie pas : remplDen reste 0 => 0 %, ce qui signale l'absence de capacités
  // saisies (0 déclenche volontairement le tone rouge). Dès qu'une colonne capacite_max
  // sera ajoutée (migration + SELECT), le taux réel s'affichera automatiquement.
  let remplNum = 0;
  let remplDen = 0;
  rows.forEach((s) => {
    const cap = parseInt(s.capacite_max, 10) || 0;
    if (cap > 0) {
      remplNum += parseInt(s.nombre_inscrits, 10) || 0;
      remplDen += cap;
    }
  });
  const remplissage = remplDen > 0 ? (remplNum / remplDen) * 100 : 0;
  const remplColor = remplissage >= 70 ? getTone('emerald').base : remplissage >= 40 ? getTone('amber').base : getTone('rose').base;

  // Évolution mensuelle pour les deux graphes (bornée par le périmètre chargé/filtré).
  const evolution = buildMonthlyEvolution(rows);

  // Panneaux d'agrégation par dimension.
  const parProfession = buildParProfession(rows);
  const topFormations = buildTopFormations(rows);

  // Prospects normalisés une fois, puis rattachés à chaque libellé de profession du panneau.
  const normalizedProspects = vivier.byProfession
    .map((r) => ({ t: normProfession(r.profession), n: parseInt(r.vivier, 10) || 0 }))
    .filter((r) => r.t.length >= 3);
  const viverFor = (profession) => sumVivier(profession, normalizedProspects);

  // Panneaux de la 3ᵉ rangée.
  const parDept = buildParDept(rows);
  const parFormat = buildParFormat(rows);
  const statuts = buildStatuts(rows);
  const remplissageBuckets = buildRemplissage(rows);

  // Vivier par département : rattachement par clé exacte (même logique CP→dept des deux côtés).
  const vivierDeptMap = {};
  vivierDept.byDepartment.forEach((r) => { vivierDeptMap[r.dept] = parseInt(r.vivier, 10) || 0; });
  const viverForDept = (dept) => (Object.prototype.hasOwnProperty.call(vivierDeptMap, dept) ? vivierDeptMap[dept] : undefined);

  // Assiduité : totaux du périmètre + déclinaisons no-show (profession / format / jour / dept).
  const totals = rows.reduce((acc, s) => { accumule(acc, s); return acc; }, emptyAgg());
  const globalTauxNoShow = tauxNoShow(totals);
  // Taux d'abandon : dénominateur = population de départ (les abandons ayant été retirés des
  // inscrits actifs, on les réintègre) → abandons / (inscrits + abandons).
  const tauxAbandon = (totals.inscrits + totals.abandons) > 0
    ? (totals.abandons / (totals.inscrits + totals.abandons)) * 100
    : 0;
  const nsProfession = toNoShowItems(parProfession, (a) => a.profession, 8);
  const nsDept = toNoShowItems(parDept, (a) => `Dépt ${a.dept}`, 8);
  const nsFormat = toNoShowItems(parFormat, (a) => a.format, 0);
  const nsJour = buildParJour(rows);

  // Rentabilité : donut par palier + top 5 / flop 5 sessions (centre = marge globale déjà calculée).
  const rentabilite = buildRentabilite(rows);
  const openSession = (id) => navigate(`/dashboard/session/${id}`);

  return (
    <>
      {/* Bandeau KPI */}
    <Grid container spacing={2}>
      <Grid item xs={12} sm={6} md={4} lg={2}>
        <AnalyseCard
          label="Sessions"
          value={sessionsCount}
          color={getTone('orange').base}
          icon={<LayersRounded sx={{ fontSize: '1.3rem' }} />}
          subtitle={cancelledLabel}
          sparkline={sessionsSeries}
        />
      </Grid>
      <Grid item xs={12} sm={6} md={4} lg={2}>
        <AnalyseCard
          label="Inscrits"
          value={inscritsTotal}
          color={getTone('orange').base}
          icon={<PeopleAltRounded sx={{ fontSize: '1.3rem' }} />}
          subtitle={`${avgPerSession.toFixed(1)} / session`}
          sparkline={inscritsSeries}
        />
      </Grid>
      <Grid item xs={12} sm={6} md={4} lg={2}>
        <AnalyseCard
          label="CA prévisionnel"
          value={caPrevisionnel}
          color={getTone('emerald').base}
          icon={<AccountBalanceWalletRounded sx={{ fontSize: '1.3rem' }} />}
          isCurrency
          sparkline={caSeries}
        />
      </Grid>
      <Grid item xs={12} sm={6} md={4} lg={2}>
        <AnalyseCard
          label="Résultat cumulé"
          value={resultatCumule}
          color={resultColor}
          icon={<TrendingUpRounded sx={{ fontSize: '1.3rem' }} />}
          isCurrency
          signed
          sparkline={resultatSeries}
        />
      </Grid>
      <Grid item xs={12} sm={6} md={4} lg={2}>
        <AnalyseCard
          label="Marge moyenne"
          value={marge}
          color={margeColor}
          icon={<PercentRounded sx={{ fontSize: '1.3rem' }} />}
          isPercent
        />
      </Grid>
      <Grid item xs={12} sm={6} md={4} lg={2}>
        <AnalyseCard
          label="Remplissage moyen"
          value={remplissage}
          color={remplColor}
          icon={<SpeedRounded sx={{ fontSize: '1.3rem' }} />}
          isPercent
        />
      </Grid>
      </Grid>

      {/* Graphes d'évolution mensuelle */}
      <Grid container spacing={2} sx={{ mt: 0.5 }}>
        <Grid item xs={12} md={6}>
          <Card sx={PANEL_CARD_SX}>
            <ChartHeader icon={<ShowChartRounded sx={{ fontSize: '1.15rem' }} />} title="Activité mensuelle" />
            <LineChart
              labels={evolution.labels}
              height={200}
              series={[
                { label: 'Sessions', data: evolution.sessions },
                { label: 'Inscrits', data: evolution.inscrits, color: getTone('blue').base }
              ]}
            />
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card sx={PANEL_CARD_SX}>
            <ChartHeader icon={<StackedLineChartRounded sx={{ fontSize: '1.15rem' }} />} title="CA & Bénéfice mensuels" />
            <LineChart
              labels={evolution.labels}
              height={200}
              valueFormat={eur}
              series={[
                { label: 'CA', data: evolution.ca },
                { label: 'Bénéfice', data: evolution.benef, color: getTone('emerald').base }
              ]}
            />
          </Card>
        </Grid>
      </Grid>

      {/* Panneaux d'agrégation : profession × vivier / top formations */}
      <Grid container spacing={2} sx={{ mt: 0.5 }}>
        <Grid item xs={12} md={6}>
          <ParProfessionPanel data={parProfession} viverFor={viverFor} totalVivier={vivier.total} />
        </Grid>
        <Grid item xs={12} md={6}>
          <TopFormationsPanel data={topFormations} />
        </Grid>
      </Grid>

      {/* Panneaux : départements × vivier / formats / statuts & remplissage */}
      <Grid container spacing={2} sx={{ mt: 0.5 }}>
        <Grid item xs={12} md={4}>
          <TopDepartementsPanel data={parDept} viverFor={viverForDept} />
        </Grid>
        <Grid item xs={12} md={4}>
          <FormatsPanel data={parFormat} />
        </Grid>
        <Grid item xs={12} md={4}>
          <StatutsRemplissagePanel statuts={statuts} remplissage={remplissageBuckets} />
        </Grid>
      </Grid>

      {/* Assiduité globale + no-show par profession / format & jour / département */}
      <Grid container spacing={2} sx={{ mt: 0.5 }}>
        <Grid item xs={12} md={3}>
          <AssiduiteGlobalePanel totals={totals} taux={globalTauxNoShow} tauxAbandon={tauxAbandon} evolution={evolution} />
        </Grid>
        <Grid item xs={12} md={3}>
          <NoShowListPanel icon={<PersonOffRounded sx={{ fontSize: '1.15rem' }} />} title="No-show par profession" items={nsProfession} />
        </Grid>
        <Grid item xs={12} md={3}>
          <NoShowFormatJourPanel formatItems={nsFormat} jourItems={nsJour} />
        </Grid>
        <Grid item xs={12} md={3}>
          <NoShowListPanel
            icon={<PlaceRounded sx={{ fontSize: '1.15rem' }} />}
            title="No-show par département"
            items={nsDept}
            footer="Taux = absents / inscrits attendus (annulés exclus). Min. 5 inscrits."
          />
        </Grid>
      </Grid>

      {/* Remplissage par département / profession / thématique */}
      <Grid container spacing={2} sx={{ mt: 0.5 }}>
        <Grid item xs={12} md={4}>
          <RemplissagePanel
            icon={<PlaceRounded sx={{ fontSize: '1.15rem' }} />}
            title="Remplissage par département"
            data={parDept}
            fallback="Pas assez de places offertes localisées."
            footer="Trié du moins rempli au mieux rempli — les premiers sont à travailler."
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <RemplissagePanel
            icon={<WorkOutlineRounded sx={{ fontSize: '1.15rem' }} />}
            title="Remplissage par profession"
            data={parProfession}
            fallback="Pas assez de places offertes."
            footer="Trié du moins rempli au mieux rempli — les premiers sont à travailler."
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <RemplissagePanel
            icon={<CategoryRounded sx={{ fontSize: '1.15rem' }} />}
            title="Remplissage par thématique"
            data={[]}
            fallback="Thématiques non renseignées sur les formations (catégorie ou modules)."
            footer="Thématique = 1re thématique renseignée dans les modules du programme."
          />
        </Grid>
      </Grid>

      {/* Rentabilité : donut par palier + top 5 / sessions à surveiller */}
      <Grid container spacing={2} sx={{ mt: 0.5 }}>
        <Grid item xs={12} md={4}>
          <RentabilitePanel donut={rentabilite.donut} marge={marge} />
        </Grid>
        <Grid item xs={12} md={4}>
          <SessionListPanel icon={<EmojiEventsRounded sx={{ fontSize: '1.15rem' }} />} title="Top 5 sessions (bénéfice)" items={rentabilite.top} onOpen={openSession} />
        </Grid>
        <Grid item xs={12} md={4}>
          <SessionListPanel icon={<WarningAmberRounded sx={{ fontSize: '1.15rem' }} />} title="5 sessions à surveiller (perte)" items={rentabilite.flop} onOpen={openSession} />
        </Grid>
      </Grid>
    </>
  );
};

SessionFormationsAnalyse.propTypes = {
  sessions: PropTypes.array
};

export default SessionFormationsAnalyse;
